/**
 * Seed references: how level manifests pull copy from `content.seed.json`
 * without duplicating it (SPEC §4.4, A-38).
 *
 * Syntax: `{ "$ref": "seed:roles/riot-games/zones/Zone_Staff_2025" }`.
 * - Object segments are keys.
 * - Array segments match an element's `level` or `id` field, never an index.
 *   Roles are ordered newest-first, so index-based refs would silently point
 *   at the wrong role the day a new job is added at the top.
 *
 * A ref object may carry sibling keys: they are overrides, shallow-merged onto
 * the resolved object, and may themselves be refs (A-05). Refs only resolve
 * against the seed, which contains no refs, so cycles are impossible.
 */

const SCHEME = "seed:";

export class RefError extends Error {
  override name = "RefError";
}

interface RefObject {
  readonly $ref: string;
  readonly [override: string]: unknown;
}

export function isRefObject(value: unknown): value is RefObject {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as { $ref?: unknown }).$ref === "string"
  );
}

/** Resolves one `seed:` path to the value it points at (a deep copy, safe to keep). */
export function resolveSeedPath(seed: unknown, ref: string): unknown {
  if (!ref.startsWith(SCHEME)) {
    throw new RefError(`Unsupported ref '${ref}': refs must start with '${SCHEME}'`);
  }

  const segments = ref.slice(SCHEME.length).split("/").filter(Boolean);
  let current: unknown = seed;

  segments.forEach((segment, i) => {
    const next = step(current, segment);
    if (next === undefined) {
      const where = segments.slice(0, i).join("/") || "(root)";
      throw new RefError(`Unresolved ref '${ref}': no '${segment}' in ${where}`);
    }
    current = next;
  });

  return structuredClone(current);
}

/** Returns a copy of `value` with every ref object resolved against the seed. */
export function resolveRefs(value: unknown, seed: unknown): unknown {
  if (isRefObject(value)) return resolveRefObject(value, seed);
  if (Array.isArray(value)) return value.map((item) => resolveRefs(item, seed));

  if (typeof value === "object" && value !== null) {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [key, resolveRefs(child, seed)]),
    );
  }

  return value;
}

function resolveRefObject(refObject: RefObject, seed: unknown): unknown {
  const { $ref, ...overrides } = refObject;
  const target = resolveSeedPath(seed, $ref);
  if (Object.keys(overrides).length === 0) return target;

  if (typeof target !== "object" || target === null || Array.isArray(target)) {
    throw new RefError(`Ref '${$ref}' has overrides but does not resolve to an object`);
  }

  const resolvedOverrides = resolveRefs(overrides, seed) as Record<string, unknown>;
  return { ...(target as Record<string, unknown>), ...resolvedOverrides };
}

/** One path step: a key into an object, or an id/level match into an array. */
function step(container: unknown, segment: string): unknown {
  if (Array.isArray(container)) {
    return container.find((item: unknown) => matchesKey(item, segment));
  }
  if (typeof container === "object" && container !== null && Object.hasOwn(container, segment)) {
    return (container as Record<string, unknown>)[segment];
  }
  return undefined;
}

function matchesKey(item: unknown, segment: string): boolean {
  if (typeof item !== "object" || item === null) return false;
  const { level, id } = item as { level?: unknown; id?: unknown };
  return level === segment || id === segment;
}
